'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Check, X, Crown, Sparkles, ArrowRight, Zap } from 'lucide-react';

type BillingCycle = 'monthly' | 'annual';

const tiers = [
  {
    id: 'tier-free',
    name: 'FREE',
    tagline: 'Start your journey',
    price: { monthly: 0, annual: 0 },
    currency: '',
    cta: 'Get started free',
    ctaHref: '/onboarding-questionnaire',
    featured: false,
    badge: null,
    color: '#6B6560',
    features: [
      { id: 'f-questionnaire', label: 'Full personalised questionnaire', included: true },
      { id: 'f-pattern', label: 'Your belief pattern assignment', included: true },
      { id: 'f-resets', label: '10-second cue + 60-second reset', included: true },
      { id: 'f-meditation-1', label: '1 personalised guided session', included: true },
      { id: 'f-audio-1', label: 'Narrated audio session', included: true },
      { id: 'f-meditations-more', label: 'Additional sessions', included: false },
      { id: 'f-evolving', label: 'Evolving practice (adapts as you grow)', included: false },
      { id: 'f-whatsapp', label: 'Daily resets via WhatsApp', included: false },
      { id: 'f-tracking', label: 'Pattern progress tracking', included: false },
      { id: 'f-guidance', label: 'Daily personalised guidance', included: false },
      { id: 'f-priority', label: 'Priority session generation', included: false },
    ],
  },
  {
    id: 'tier-plus',
    name: 'PLUS',
    tagline: 'Build a real practice',
    price: { monthly: 12, annual: 9 },
    currency: 'USD',
    cta: 'Start PLUS free for 7 days',
    ctaHref: '/detail-capture-account-creation',
    featured: true,
    badge: 'Most popular',
    color: '#2A9D8F',
    features: [
      { id: 'fp-questionnaire', label: 'Full personalised questionnaire', included: true },
      { id: 'fp-pattern', label: 'Your belief pattern assignment', included: true },
      { id: 'fp-resets', label: '10-second cue + 60-second reset', included: true },
      { id: 'fp-meditation-1', label: '1 personalised guided session', included: true },
      { id: 'fp-audio-1', label: 'Narrated audio session', included: true },
      { id: 'fp-meditations-more', label: '12 additional sessions', included: true },
      { id: 'fp-evolving', label: 'Evolving practice (adapts as you grow)', included: true },
      { id: 'fp-whatsapp', label: 'Daily resets via WhatsApp', included: true },
      { id: 'fp-tracking', label: 'Pattern progress tracking', included: true },
      { id: 'fp-guidance', label: 'Daily personalised guidance', included: false },
      { id: 'fp-priority', label: 'Priority session generation', included: false },
    ],
  },
  {
    id: 'tier-premium',
    name: 'PREMIUM',
    tagline: 'Deep, lasting transformation',
    price: { monthly: 29, annual: 22 },
    currency: 'USD',
    cta: 'Start PREMIUM free for 7 days',
    ctaHref: '/detail-capture-account-creation',
    featured: false,
    badge: 'Full experience',
    color: '#B8860B',
    features: [
      { id: 'fpr-questionnaire', label: 'Full personalised questionnaire', included: true },
      { id: 'fpr-pattern', label: 'Your belief pattern assignment', included: true },
      { id: 'fpr-resets', label: '10-second cue + 60-second reset', included: true },
      { id: 'fpr-meditation-1', label: '1 personalised guided session', included: true },
      { id: 'fpr-audio-1', label: 'Narrated audio session', included: true },
      { id: 'fpr-meditations-more', label: 'Unlimited sessions', included: true },
      { id: 'fpr-evolving', label: 'Evolving practice (adapts as you grow)', included: true },
      { id: 'fpr-whatsapp', label: 'Daily resets via WhatsApp', included: true },
      { id: 'fpr-tracking', label: 'Advanced pattern progress tracking', included: true },
      { id: 'fpr-guidance', label: 'Daily personalised guidance', included: true },
      { id: 'fpr-priority', label: 'Priority session generation', included: true },
    ],
  },
];

export default function PricingContent() {
  const [billing, setBilling] = useState<BillingCycle>('annual');

  return (
    <div className="pt-24 pb-20 px-6 max-w-screen-lg mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-[#2A9D8F]/10 rounded-full px-4 py-1.5 text-sm font-semibold text-[#2A9D8F] mb-5">
          <Crown size={14} />
          Choose your plan
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1C1917] mb-4">
          Invest in the voice inside your head.
        </h1>
        <p className="text-[#6B6560] max-w-xl mx-auto leading-relaxed">
          Start free — no card needed. Upgrade when you&apos;re ready for a deeper, evolving practice.
        </p>

        {/* Billing toggle */}
        <div className="inline-flex items-center gap-1 bg-[#EAE6E0] rounded-full p-1 mt-8">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              billing === 'monthly' ?'bg-white text-[#1C1917] shadow-sm' :'text-[#6B6560] hover:text-[#1C1917]'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling('annual')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
              billing === 'annual' ?'bg-white text-[#1C1917] shadow-sm' :'text-[#6B6560] hover:text-[#1C1917]'
            }`}
          >
            Annual
            <span className="text-xs font-bold text-[#52B788] bg-[#52B788]/10 px-1.5 py-0.5 rounded-full">
              Save 25%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={`tier-card flex flex-col ${tier.featured ? 'featured' : ''}`}
          >
            {/* Badge */}
            {tier.badge && (
              <div
                className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-bold px-4 py-1.5 rounded-full"
                style={{
                  background: tier.featured ? '#2A9D8F' : '#F5F0E0',
                  color: tier.featured ? 'white' : '#B8860B',
                  border: tier.featured ? 'none' : '1px solid #E5D5A0',
                }}
              >
                {tier.featured && <Sparkles size={10} className="inline mr-1" />}
                {tier.badge}
              </div>
            )}

            {/* Tier header */}
            <div className="mb-6">
              <p
                className="text-xs font-black uppercase tracking-widest mb-1"
                style={{ color: tier.color }}
              >
                {tier.name}
              </p>
              <p className="text-sm text-[#6B6560] mb-4">{tier.tagline}</p>
              <div className="flex items-end gap-1">
                {tier.price[billing] === 0 ? (
                  <span className="text-4xl font-black text-[#1C1917]">Free</span>
                ) : (
                  <>
                    <span className="text-4xl font-black text-[#1C1917]">
                      ${tier.price[billing]}
                    </span>
                    <span className="text-sm text-[#9B9590] mb-1.5">/ month</span>
                  </>
                )}
              </div>
              {billing === 'annual' && tier.price.annual > 0 && (
                <p className="text-xs text-[#9B9590] mt-1">
                  Billed ${tier.price.annual * 12} annually
                </p>
              )}
            </div>

            {/* CTA */}
            <Link
              href={tier.ctaHref}
              className={`flex items-center justify-center gap-2 py-3 px-4 rounded-2xl text-sm font-bold mb-6 transition-all ${
                tier.featured
                  ? 'btn-primary'
                  : tier.name === 'PREMIUM' ?'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100' :'bg-[#F5F2ED] text-[#4A4540] border border-[#EAE6E0] hover:bg-[#EAE6E0]'
              }`}
              style={tier.featured ? {} : { minHeight: '48px' }}
            >
              {tier.name === 'PREMIUM' && <Crown size={14} />}
              {tier.name === 'PLUS' && <Zap size={14} />}
              {tier.cta}
              <ArrowRight size={14} />
            </Link>

            {/* Features */}
            <div className="space-y-3 flex-1">
              {tier.features.map((feat) => (
                <div key={feat.id} className="flex items-start gap-2.5">
                  {feat.included ? (
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${tier.color}15` }}
                    >
                      <Check size={11} style={{ color: tier.color }} strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[#F0EDE8] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X size={10} className="text-[#C5C0BA]" strokeWidth={2.5} />
                    </div>
                  )}
                  <span
                    className={`text-sm leading-snug ${
                      feat.included ? 'text-[#4A4540]' : 'text-[#C5C0BA]'
                    }`}
                  >
                    {feat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Own-voice hint */}
      <div className="max-w-2xl mx-auto mb-12 p-5 bg-[#F5F2ED] rounded-2xl border border-[#EAE6E0] text-center">
        <p className="text-sm text-[#6B6560] leading-relaxed">
          <span className="font-semibold text-[#1C1917]">Coming soon for PREMIUM:</span>{' '}
          Later, you&apos;ll be able to generate sessions in a voice that sounds like your own,
          so your practice feels even more personal.
        </p>
      </div>

      {/* FAQ-style reassurances */}
      <div className="max-w-2xl mx-auto">
        <h2 className="text-xl font-bold text-[#1C1917] mb-6 text-center">Common questions</h2>
        <div className="space-y-4">
          {[
            {
              id: 'faq-trial',
              q: 'What happens after the 7-day trial?',
              a: 'You\'ll be charged the plan price at the end of your trial. Cancel any time before then — no charge, no hassle.',
            },
            {
              id: 'faq-card',
              q: 'Do I need a card to try FREE?',
              a: 'No. The FREE tier requires no card and no payment details. Ever.',
            },
            {
              id: 'faq-evolving',
              q: 'What are "evolving practice" sessions?',
              a: 'blv tracks how you use it — which resets you do, when, and how often. Over time, your sessions update to reflect your actual growth, not just your initial answers.',
            },
            {
              id: 'faq-whatsapp',
              q: 'How does WhatsApp delivery work?',
              a: 'PLUS and PREMIUM members receive a personalised daily micro-reset message on WhatsApp — timed to when you\'re most likely to need it, based on your usage patterns.',
            },
          ].map((item) => (
            <div key={item.id} className="card-elevated p-5">
              <h4 className="font-bold text-[#1C1917] mb-2 text-sm">{item.q}</h4>
              <p className="text-sm text-[#6B6560] leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}